/**
 * EN/RU headers for the terminal's `agents`, `system status` and
 * `inspect core` commands, keyed by `Freshness` and by the universe's
 * `LiveMode`. Pure module — reuses `freshnessSuffix` from
 * `freshness-label.ts` for the machine-contract suffix (the same
 * `· LIVE` / `· STALE` / `· SYNCING` / `· OFFLINE · SIM` / `· CANARY`
 * tokens used across the site); the human-readable lead phrase is fully
 * translated per locale, matching `ai/terminal.ts`'s convention that
 * machine tokens stay Latin while prose is translated.
 *
 * Two honesty rules live in the copy below:
 *  - SYNCING claims nothing. It is the state before the site knows whether
 *    the population is observer-backed or the static fallback, so it says
 *    exactly that instead of borrowing either verdict.
 *  - a canary is never described as the flagship population. It is a real
 *    running system, but its decisions may be the engine's deterministic
 *    code fallback rather than a model, and the lead phrase says so.
 *
 * Pure module: no Next/React/store imports — importable directly under
 * `node --test --experimental-strip-types`.
 */

import type { Freshness, LiveMode } from './types.ts';
import { freshnessSuffix, showsCanary } from './freshness-label.ts';
import type { Lang } from '../i18n/config';

type ByFreshness = Record<Freshness, string>;

const AGENTS_HEAD: Record<Lang, ByFreshness> = {
  en: {
    LIVE: 'AGENTS — real NanoAgent population, chain-verified',
    STALE: 'AGENTS — chain-verified run, currently stale',
    SYNCING: 'AGENTS — source not established yet, connecting to the observer',
    OFFLINE: 'AGENTS — ambient simulation of the laboratory working structure',
  },
  ru: {
    LIVE: 'АГЕНТЫ — реальная популяция NanoAgent, проверено по цепочке',
    STALE: 'АГЕНТЫ — цепочка проверена, сейчас без обновлений',
    SYNCING: 'АГЕНТЫ — источник ещё не установлен, идёт подключение к наблюдателю',
    OFFLINE: 'АГЕНТЫ — фоновая симуляция рабочей структуры лаборатории',
  },
};

/** Lead phrase for an observer-backed run that is NOT the flagship universe. */
const AGENTS_HEAD_CANARY: Record<Lang, string> = {
  en: 'AGENTS — engineering canary population, code fallback possible',
  ru: 'АГЕНТЫ — популяция инженерной канарейки, возможны решения кода',
};

const STATUS_AGENTS_LABEL: Record<Lang, ByFreshness> = {
  en: {
    LIVE: 'agents',
    STALE: 'agents',
    SYNCING: 'agents',
    OFFLINE: 'agents [SIM]',
  },
  ru: {
    LIVE: 'агенты',
    STALE: 'агенты',
    SYNCING: 'агенты',
    OFFLINE: 'агенты [SIM]',
  },
};

/** Full header line for the `agents` terminal command: lead phrase + machine suffix. */
export function agentsHeaderLine(lang: Lang, freshness: Freshness, mode?: LiveMode | null): string {
  const lead = showsCanary(freshness, mode) ? AGENTS_HEAD_CANARY[lang] : AGENTS_HEAD[lang][freshness];
  return `${lead} ${freshnessSuffix(freshness, mode)}`;
}

/** Row label for the agent count in `system status`, localized, tagged [SIM] only when OFFLINE. */
export function statusAgentsLabel(lang: Lang, freshness: Freshness): string {
  return STATUS_AGENTS_LABEL[lang][freshness];
}

/**
 * What the terminal actually knows about the population size.
 * `acting` and `shown` describe the rows on screen; `population` is the
 * universe's own active-agent count as reported by the bridge, which can be
 * larger than `shown` because the served roster is capped.
 */
export interface AgentCountFacts {
  acting: number;
  shown: number;
  population?: number | null;
}

const COUNT_WORDS: Record<Lang, { acting: string; shownWord: string; inUniverse: string }> = {
  en: { acting: 'active of', shownWord: 'shown', inUniverse: 'in the universe' },
  ru: { acting: 'активных из', shownWord: 'показано', inUniverse: 'во вселенной' },
};

/**
 * Row value for the agent count in `system status`.
 *
 * Never a bare roster length: "active agents 10" over a roster of 3 acting
 * and 7 idle agents was a false statement, and a roster capped below the
 * real population would under-report it silently. The value states how many
 * rows are acting, how many rows there are, and — only when they differ —
 * how large the universe actually is.
 */
export function statusAgentsValue(
  lang: Lang,
  freshness: Freshness,
  facts: AgentCountFacts,
  mode?: LiveMode | null,
): string {
  const w = COUNT_WORDS[lang];
  const suffix = freshnessSuffix(freshness, mode);
  const capped =
    typeof facts.population === 'number' && Number.isFinite(facts.population) && facts.population > facts.shown;
  const head = `${facts.acting} ${w.acting} ${facts.shown}`;
  return capped
    ? `${head} ${w.shownWord} · ${facts.population} ${w.inUniverse} ${suffix}`
    : `${head} ${suffix}`;
}

const CORE_NOTE: Record<Lang, ByFreshness> = {
  en: {
    LIVE: 'the agents around it are the real NanoAgent population, chain-verified against the running universe.',
    STALE:
      'the agents around it are the last chain-verified NanoAgent population; the universe is not currently progressing.',
    SYNCING:
      'the source of the agents around it is not established yet — the site is still connecting to the observer.',
    OFFLINE: 'the agents around it are simulated ambience mirroring the real working structure.',
  },
  ru: {
    LIVE: 'агенты вокруг — реальная популяция NanoAgent, проверенная по цепочке относительно работающей вселенной.',
    STALE:
      'агенты вокруг — последняя проверенная по цепочке популяция NanoAgent; вселенная сейчас не продвигается.',
    SYNCING:
      'источник агентов вокруг ещё не установлен — сайт продолжает подключаться к наблюдателю.',
    OFFLINE: 'агенты вокруг — симулированная фоновая среда, отражающая реальную рабочую структуру.',
  },
};

const CORE_NOTE_CANARY: Record<Lang, string> = {
  en: 'the agents around it are an observer-backed engineering canary, not the flagship universe — some of its decisions may be the engine’s code fallback rather than a model.',
  ru: 'агенты вокруг — инженерная канарейка на данных обсервера, а не флагманская вселенная: часть её решений может быть кодовым резервом движка, а не моделью.',
};

/** Full note line for the `inspect core` terminal command: lead phrase + machine suffix. */
export function coreNoteLine(lang: Lang, freshness: Freshness, mode?: LiveMode | null): string {
  const lead = showsCanary(freshness, mode) ? CORE_NOTE_CANARY[lang] : CORE_NOTE[lang][freshness];
  return `${lead} ${freshnessSuffix(freshness, mode)}`;
}
